import { InfoBox, OverlayContainer, BigButton, GradientIcon, Checkbox } from "@kalyzee/escaet-ui";
import { TextLimitationTextarea } from "@kalyzee/kast-web-sdk/web-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmableEscaetPedagogyAction, ConfirmableEscaetPedagogyActionDataType, EscaetPedagogyAction } from "../../interfaces/pedagogy";

import styles from "./MessageForm.module.css";

export interface PedagogyMessageFormProps <T extends keyof ConfirmableEscaetPedagogyActionDataType> {
  actions?: EscaetPedagogyAction[];
  confirmableAction?: ConfirmableEscaetPedagogyAction<T>;
  predefinedMessages?: {
    [actionType: string]: {label: string, message: string}[],
  };
  onClose?: () => void;
}

const TextContent = ({
  action,
  predefinedMessages,
  value,
  onChange
}: {
  action?: EscaetPedagogyAction,
  predefinedMessages?: {
    [actionType: string]: {label: string, message: string}[],
  };
  value?: string;
  onChange?: (value?: string) => void;
}) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.currentTarget.value);
    },
    [onChange]
  );

  const renderPredefinedMessages = () => {
    const actionType = action?.type;
    if (!actionType) return null;
    const labels = predefinedMessages?.[actionType];
    if (!labels?.length) return null;

    const renderLabel = (label: string, message: string) => {
      return (
        <BigButton
          key={`action_${actionType}_${label}`}
            onClick={() => {
              onChange?.(message);
            }}
            style={{
              backgroundColor: "grey",
              padding: "0.5em",
              color: "white",
              width: "fit-content",
              fontSize: "0.75em",
              gap: "0",
            }}
            text={label}
          />
      )
    };

    return (
      <div style={{ width: "100%" }}>
        <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: "600" }}>Messages prédéfinis :</div>
        <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "10px" }}>
          {labels.map((l) => renderLabel(l.label, l.message))}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: "#F1EFEB",
        display: "flex",
        flexDirection: "column",
        gap: "0.75em",
        width: "100%",
        height: "100%",
        alignItems: "center",
      }}
    >
      <TextLimitationTextarea
        value={value}
        onChange={handleChange}
        limit={255}
        showIndicator={false}
        style={{
          flex: 1,
          width: "100%",
        }}
        textElementStyle={{
          height: "100%",
          resize: "none",
        }}
        placeholder="Mon message ..."
      />
      {renderPredefinedMessages()}
  </div>
  );
};

const CheckboxContent = ({
  value,
  onChange,
  message = "Confirmer l'action",
}: {
  value?: boolean;
  onChange?: (value?: boolean) => void;
  message?: string;
}) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.currentTarget.checked);
    },
    [onChange]
  );

  useEffect(() => {

  }, [onChange]);

  return (
    <div
      style={{
        backgroundColor: "#F1EFEB",
        padding: "2em",
        display: "flex",
        flexDirection: "column",
        gap: "0.75em",
        width: "100%",
        alignItems: "center",
      }}
    >
      <Checkbox
        id="confirm-action-checkbox"
        label={message}
        className={`${styles.roomCheckboxLabel}`}
        boxClassName={styles.checkbox}
        checkedBoxClassName={styles.selected}
        innerBoxClassName={styles.checkboxInner}
        checked={!!value}
        onChange={handleChange}
      />
    </div>
  );
}

export const PedagogyMessageForm = <T extends keyof ConfirmableEscaetPedagogyActionDataType = any> ({ predefinedMessages, actions, confirmableAction, onClose }: PedagogyMessageFormProps<T>) => {
  const [value, setValue] = useState<ConfirmableEscaetPedagogyActionDataType[T]>();

  const originAction: EscaetPedagogyAction | undefined = useMemo(() => {
    if (!confirmableAction) return undefined;
    return actions?.find((a) => a.type === confirmableAction.actionType);
  }, [actions, confirmableAction]);

  const _onClose = useCallback(() => {
    onClose?.();
    setValue(undefined);
  }, [onClose]);

  const onSend = useCallback(() => {
    const success = confirmableAction?.finalize(value);
    if (success) _onClose();
  }, [confirmableAction, _onClose, value]);

  if (!originAction || !confirmableAction) return null;

  const users = confirmableAction.users;

  const renderForm = () => {
    if (confirmableAction.dataType === "message") {
      return (
        <TextContent
          action={originAction}
          predefinedMessages={predefinedMessages}
          value={value as string}
          onChange={(v) => setValue(v as ConfirmableEscaetPedagogyActionDataType[T])}
        />
      );
    }
    if (confirmableAction.dataType === "check") {
      return (
        <CheckboxContent
          value={value as boolean}
          onChange={(v) => setValue(v as ConfirmableEscaetPedagogyActionDataType[T])}
          message={confirmableAction.customMessage}
        />
      );
    }
    return null;
  }
  
  const actionName = typeof originAction.name === "function" ? originAction.name(users) : originAction.name;

  return (
    <OverlayContainer style={{ width: "100%", height: "100%", position: "fixed" }}>
      <InfoBox
        onClose={onClose}
        style={{
          width: "min(90%, 600px)",
          height: 300,
        }}
        Header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontWeight: 800,
              fontSize: "1.25em",
            }}
          >
            <GradientIcon
              style={{
                width: "1.5em",
                height: "1.5em",
                padding: "0.375em",
              }}
              icon={originAction.Icon}
              background={{
                mode: "fading_gradient",
                color: originAction.color,
                top: 0.2,
              }}
            />
            <div>
              {actionName} {users.length} participant{users.length > 1 ? "s" : ""}
            </div>
          </div>
        }
        Content={
          <div
            style={{
              backgroundColor: "#F1EFEB",
              padding: "2em",
              display: "flex",
              flexDirection: "column",
              gap: "0.75em",
              width: "100%",
              alignItems: "center",
            }}
          >
            {renderForm()}
            <BigButton
              onClick={onSend}
              style={{
                backgroundColor: originAction.color,
                padding: "0.5em",
                paddingLeft: "0.75em",
                color: "white",
                width: "fit-content",
                fontSize: "0.75em",
              }}
              badgeStyle={{
                color: originAction.color,
                lineHeight: 0,
                fontSize: "0.75em",
              }}
              icon={<div style={{ width: "1.5em", height: "1.5em" }}>{originAction.Icon}</div>}
              text={originAction.type === "notify" ? "Envoyer le message" : actionName}
              badgeText={`${users.length}`}
            />
          </div>
        }
      />
    </OverlayContainer>
  );
};
